/**
 * Draws whatever is in the toast store (use-toast) as Radix toasts, named
 * for screen readers in the app's language (Radix's own names are English).
 * A title or description that is plain text goes through FailureText, so
 * the standard failure sentence's support address is a mailto link in every
 * toast (shared/components/ServerFailure).
 *
 * Used by: Layout, and the account-deletion page (DeleteAccountPage), which
 * sits outside it.
 *
 * @module toaster
 */
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/shared/components/ui/toast"
import { useToast } from "@/shared/components/ui/use-toast"
import { useTranslation } from "@/shared/hooks/useTranslation"
import type { ReactNode } from "react"
import { FailureText } from "@/shared/components/ServerFailure"

/**
 * A toast's title or description: plain text with the support address made a
 * link, anything else as it is.
 *
 * @param content - What the toast was given.
 * @returns What to draw.
 */
function linked(content: ReactNode): ReactNode {
  return typeof content === 'string' ? <FailureText text={content} /> : content
}

/** The toasts on screen now, each with its close button. */
export function Toaster(): JSX.Element {
  const { toasts } = useToast()
  const { reactTranslate: t } = useTranslation()

  return (
    <ToastProvider label={t('sitewide.notification')}>
      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <Toast key={id} {...props}>
            <div className="grid gap-1">
              {title && <ToastTitle>{linked(title)}</ToastTitle>}
              {description && (
                <ToastDescription>{linked(description)}</ToastDescription>
              )}
            </div>
            {action}
            {/* An icon alone, so it needs a name to be read out by. */}
            <ToastClose aria-label={t('sitewide.close')} />
          </Toast>
        )
      })}
      {/* Radix puts the shortcut in place of {hotkey}. */}
      <ToastViewport label={t('sitewide.notifications')} />
    </ToastProvider>
  )
} 